/**
 * Policy record page (OR-1665 … OR-1668, design OR-1639).
 *
 *   ┌───────────────────────────────────────────────────────────────┐
 *   │ ← Named insured [status]                   [History] [Actions▾]│
 *   │ PY 25-26  PY 24-25  PY 23-24      ← term strip                 │
 *   ├───────────────────────────────────────────────────────────────┤
 *   │ Policy  S0079PK…   │       Premium in force  Limit  SIR        │
 *   │ Effective · Brokerage · Broker                                 │
 *   ├───────────────────────────────────────────────────────────────┤
 *   │ [Facilities][Subjectivities][Premium][Files]      [Search]     │
 *   │ tab body                                                       │
 *   └───────────────────────────────────────────────────────────────┘
 *
 * Every part reads the real backend (OR-1620, OR-1625).
 *
 * Built so far: the shell and the Facilities tab (OR-1665) and the
 * Subjectivities tab (OR-1666), the Premium tab (OR-1667, directional per
 * the design) and the Files tab (OR-1668); History and Actions are out of scope for the MVP build
 * (their flows are not defined), so both buttons render disabled.
 *
 * `:policyId` is the policy (the lineage root); `?term=` picks the term the
 * record reads — the list row passes the current one. The backend reads a
 * record by term, so without `?term=` there is nothing to load.
 */
import { useEffect, useState } from "react";
import { ArrowLeft, Building07, CheckDone01, ChevronDown, ClockRewind, CurrencyDollarCircle, Folder, RefreshCw05, SearchLg, Shield01 } from "@untitledui/icons";
import { useNavigate, useSearchParams } from "react-router";
import { Button, Input, NativeSelect, Tabs } from "@tricura/atlas-ui";
import { OrionSidebar } from "../../components/orion-sidebar";
import { Skeleton } from "../../components/skeleton";
import { PolicyStatusBadge } from "../policies-list/status-badge";
import { FacilitiesTab } from "./facilities-tab";
import { FilesTab } from "./files-tab";
import { PremiumTab } from "./premium-tab";
import { SubjectivitiesTab } from "./subjectivities-tab";
import { TabState } from "./tab-state";
import { formatDate, limitPair, moneyCompact, moneyFull, policyYearLabel } from "./format";
import { type PolicyTermRecord, getPolicyTerm } from "../../api";

type RecordTab = "facilities" | "subjectivities" | "premium" | "files";

const RECORD_TABS = [
    { id: "facilities", label: "Facilities", icon: Building07 },
    { id: "subjectivities", label: "Subjectivities", icon: CheckDone01 },
    { id: "premium", label: "Premium", icon: CurrencyDollarCircle },
    { id: "files", label: "Files", icon: Folder },
];

/** Tabs whose design has no search box (Subjectivities, per the design). */
const NO_SEARCH = new Set<RecordTab>(["subjectivities"]);

type Load = { status: "loading" } | { status: "ready"; record: PolicyTermRecord } | { status: "error" };

export function PolicyDetailPage() {
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const termParam = params.get("term");
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    const [tab, setTab] = useState<RecordTab>("facilities");
    const [search, setSearch] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        (termParam ? getPolicyTerm(termParam) : Promise.reject(new Error("No term to load")))
            .then((record) => !cancelled && setLoad({ status: "ready", record }))
            .catch(() => !cancelled && setLoad({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termParam, reloadKey]);

    // Each tab searches its own rows.
    useEffect(() => setSearch(""), [tab]);

    const record = load.status === "ready" ? load.record : null;
    const selectTerm = (termId: string) => {
        const next = new URLSearchParams(params);
        next.set("term", termId);
        setParams(next, { replace: true });
    };

    return (
        <div className="bg-secondary flex min-h-screen flex-col lg:h-screen lg:flex-row">
            <OrionSidebar />

            <div className="flex min-w-0 flex-1 flex-col overflow-hidden" data-autotest="policy-detail-page">
                {/* ---------- Header ---------- */}
                <header className="flex flex-col gap-3 px-4 pt-4 md:px-6 md:pt-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2">
                            <button
                                type="button"
                                aria-label="Back to Policies"
                                onClick={() => navigate("/policies")}
                                data-autotest="policy-detail-button-back"
                                className="text-fg-quaternary hover:text-fg-quaternary_hover hover:bg-primary_hover inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md"
                            >
                                <ArrowLeft aria-hidden="true" className="size-5" />
                            </button>
                            {record ? (
                                <>
                                    <h1 className="text-primary truncate text-xl font-semibold">{record.namedInsured}</h1>
                                    <PolicyStatusBadge status={record.status} />
                                </>
                            ) : (
                                load.status === "loading" && <Skeleton className="h-7 w-64" />
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <Button color="secondary" size="sm" iconLeading={ClockRewind} isDisabled data-autotest="policy-detail-button-history">
                                History
                            </Button>
                            <Button color="primary" size="sm" iconTrailing={ChevronDown} isDisabled data-autotest="policy-detail-button-actions">
                                Actions
                            </Button>
                        </div>
                    </div>

                    {/* Term strip: PY = policy year, one per annual term. */}
                    {record && (
                        <Tabs selectedKey={record.id} onSelectionChange={(key) => selectTerm(String(key))} className="border-secondary border-b">
                            <Tabs.List
                                type="underline"
                                aria-label="Policy year"
                                items={record.terms.map((t) => ({ id: t.id, label: policyYearLabel(t.effectiveDate, t.expirationDate) }))}
                            >
                                {(t) => <Tabs.Item {...t} />}
                            </Tabs.List>
                        </Tabs>
                    )}
                </header>

                <main className="atlas-scrollbar flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-0 py-4 sm:px-6 md:pb-6">
                    {/* The record failed (or the URL has no term): say so instead of
                        leaving the skeletons up. Same layout as the tabs' error states. */}
                    {load.status === "error" ? (
                        <section className="bg-primary border-secondary flex flex-1 flex-col border-y sm:rounded-xl sm:border sm:shadow-xs">
                            <TabState
                                icon={Shield01}
                                title="Couldn't load this policy"
                                description="We had some trouble loading this policy."
                                data-autotest="policy-detail-state-error"
                                action={
                                    <div className="flex gap-3">
                                        <Button color="secondary" size="sm" onClick={() => navigate("/policies")}>
                                            Back to Policies
                                        </Button>
                                        {termParam && (
                                            <Button color="secondary" size="sm" iconTrailing={RefreshCw05} onClick={() => setReloadKey((k) => k + 1)}>
                                                Try again
                                            </Button>
                                        )}
                                    </div>
                                }
                            />
                        </section>
                    ) : (
                        <SummaryCard record={record} />
                    )}

                    {/* ---------- Tabs card ---------- */}
                    {load.status !== "error" && (
                    <section className="bg-primary border-secondary flex min-h-[480px] flex-1 flex-col overflow-hidden border-y sm:rounded-xl sm:border sm:shadow-xs">
                        <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
                            <div className="hidden md:block">
                                <Tabs selectedKey={tab} onSelectionChange={(key) => setTab(key as RecordTab)} className="w-max">
                                    <Tabs.List type="button-minimal" aria-label="Policy sections" items={RECORD_TABS}>
                                        {(t) => <Tabs.Item {...t} data-autotest={`policy-detail-tab-${t.id}`} />}
                                    </Tabs.List>
                                </Tabs>
                            </div>
                            <NativeSelect
                                size="sm"
                                aria-label="Policy section"
                                className="md:hidden"
                                value={tab}
                                onChange={(e) => setTab(e.target.value as RecordTab)}
                                options={RECORD_TABS.map((t) => ({ label: t.label, value: t.id }))}
                            />
                            {!NO_SEARCH.has(tab) && (
                                <div className="md:w-96">
                                    <Input
                                        size="sm"
                                        icon={SearchLg}
                                        placeholder="Search"
                                        value={search}
                                        onChange={setSearch}
                                        aria-label={`Search ${tab}`}
                                        data-autotest="policy-detail-input-search"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="border-secondary flex min-h-0 flex-1 flex-col border-t">
                            {tab === "facilities" && record && (
                                <FacilitiesTab
                                    termId={record.id}
                                    termStatus={record.status}
                                    search={search}
                                    submissionHref={record.sourceSubmissionId ? `/submissions/${record.sourceSubmissionId}/schedule` : null}
                                />
                            )}
                            {tab === "subjectivities" && record && <SubjectivitiesTab termId={record.id} />}
                            {tab === "files" && record && <FilesTab termId={record.id} search={search} />}
                            {tab === "premium" && record && (
                                <PremiumTab
                                    termId={record.id}
                                    search={search}
                                    submissionHref={
                                        record.sourceSubmissionId ? `/submissions/${record.sourceSubmissionId}/calculations/premium-selection` : null
                                    }
                                />
                            )}
                        </div>
                    </section>
                    )}
                </main>
            </div>
        </div>
    );
}

function SummaryCard({ record }: { record: PolicyTermRecord | null }) {
    const metrics = record
        ? [
              { label: "Premium in force", value: moneyFull(record.premiumInForce) },
              { label: "Limit", value: limitPair(record.limitPerClaim, record.limitAggregate) },
              { label: "SIR", value: moneyCompact(record.sir) },
          ]
        : [];

    // Desktop (lg+): two equal halves split by the divider in the middle,
    // 124px tall as in the design. Below lg the halves stack.
    return (
        <section
            className="bg-primary border-secondary grid grid-cols-1 gap-4 border-y px-4 py-4 sm:rounded-xl sm:border sm:shadow-xs md:px-5 lg:h-[124px] lg:grid-cols-2 lg:gap-0 lg:py-0"
            data-autotest="policy-detail-summary"
        >
            <div className="border-secondary flex min-w-0 flex-col justify-center gap-3 lg:my-6 lg:border-r lg:pr-6">
                <div className="flex items-baseline gap-2">
                    <h2 className="text-primary text-lg font-semibold">Policy</h2>
                    {record ? <span className="text-tertiary text-sm">{record.policyNumber}</span> : <Skeleton className="h-4 w-32" />}
                </div>
                <dl className="flex flex-wrap gap-x-6 gap-y-3">
                    <Field label="Effective Date" value={record ? formatDate(record.effectiveDate) : null} />
                    <Field label="Brokerage" value={record ? (record.brokerageName ?? "-") : null} />
                    <Field label="Broker" value={record ? (record.brokerName ?? "-") : null} />
                </dl>
            </div>

            <dl className="grid grid-cols-2 gap-4 sm:flex sm:flex-wrap sm:gap-8 lg:items-center lg:justify-end lg:pl-6">
                {record
                    ? metrics.map((m) => (
                          <div key={m.label} className="flex flex-col gap-1">
                              <dt className="text-tertiary text-sm">{m.label}</dt>
                              <dd className="text-primary text-lg font-semibold tabular-nums">{m.value}</dd>
                          </div>
                      ))
                    : Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-10 w-24" />)}
            </dl>
        </section>
    );
}

function Field({ label, value }: { label: string; value: string | null }) {
    return (
        <div className="flex min-w-0 flex-col gap-0.5">
            <dt className="text-secondary text-sm font-medium">{label}</dt>
            <dd className="text-tertiary truncate text-sm">{value ?? <Skeleton className="h-4 w-24" />}</dd>
        </div>
    );
}
